import { Button, IconButton, Typography } from '@mui/material';
import { isEqual } from 'lodash';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import Loading from '../common/components/Loading';
import {
    EngagementTrackerProvider,
    useEngagementTracker
} from '../common/components/person/engagementTracker/EngagementContext';
import { StepsIndexConstant } from '../common/components/Stepper';
import { useStepper } from '../common/components/StepperContext';
import { FormContext } from '../common/form/FormContext';
import { useIsWithinBreakpoints } from '../common/hooks/useIsWithinBreakpoints';
import { useI18n } from '../common/i18n/I18n';
import { Icons } from '../common/icons/Icons';
import { H, V } from '../common/Layout';
import { isEntityName } from '../common/auth/Security';
import { PEOPLE, PERSON } from '../hiring/hiring';
import MarketplaceEngagementNoteDetails from '../marketplace/MarketplaceEngagementNoteDetails';
import { MarketplaceProfileDetails } from '../marketplace/MarketplaceProfileDetails';
import PersonMarketplaceList from '../marketplace/PersonMarketplaceList';
import { useMarketplaceEngagementDetails } from '../marketplace/useMarketplaceEngagementDetails';
import ProductionDetailsPanelBody from '../production/header/ProductionSidePanelBody';
import { PersonInviteFormProps } from '../shortlists/form/inquiry/inquiry';
import PersonInviteFormItems from '../shortlists/form/PersonInviteFormItems';
import { buildPersonPrefilters } from '../shortlists/shortlistLineItem';
import { formatItemForPerson, handleItemSelection } from './hireResourceUtils';

export default function StepPersonRequestAvails({
    expandMarketplace,
    expandedContainer,
    ...props
}: PersonInviteFormProps) {
    const t = useI18n();
    const { state, validation, setState, handleChange } = useContext(FormContext);
    const { getStepData, setStepCompletion, setStepData, activeStep } = useStepper();
    const [selectedItem, setSelectedItem] = useState();
    const [detailsVisible, setDetailsVisible] = useState(false);
    const shortlistLineItems = useRef<string[]>([]);
    const resourceData = getStepData(StepsIndexConstant.zero);
    const isSmallScreen = useIsWithinBreakpoints(['xs']);
    const [isMarketplaceOpen, setIsMarketplaceOpen] = useState<boolean>(expandedContainer);
    const currentPeopleData = getStepData(activeStep);

    useEffect(() => {
        if (!isEqual(currentPeopleData, state)) {
            setStepData(activeStep, state);
        }
    }, [currentPeopleData, state]);

    useEffect(() => {
        if (!isEqual(currentPeopleData?.people, state?.people)) {
            setState({ ...state, people: state.people });
        }
    }, [state?.people, state?.people?.length]);

    useEffect(() => {
        setStepCompletion(activeStep, validation?.isValid && !props.isAttachmentsInUploadMode);
    }, [validation?.isValid, props.isAttachmentsInUploadMode]);

    useEffect(() => {
        shortlistLineItems.current = state?.people?.map((person) => person.id || person.ref.id);
    }, [state?.people]);

    useEffect(() => {
        shortlistLineItems.current = [state.person?.id || state.person?.ref?.id];
        if (!isEqual(currentPeopleData?.person, state?.person)) {
            setState({ ...state, person: state.person });
        }
    }, [state?.person]);

    const personPrefilters = useMemo(
        () =>
            buildPersonPrefilters({
                role: resourceData?.role,
                locations: resourceData?.locations
            }),
        [resourceData?.role, resourceData?.locations]
    );

    const addFromShortlist = (item) => {
        const formattedItem = formatItemForPerson(item);
        setState((prev) => updateShortlistState(prev, formattedItem, item.id));
    };

    const updateShortlistState = (prev, formattedItem, itemId) => {
        if (PEOPLE in prev) {
            const isItemInShortlist = prev?.people?.some((p) => p.id === itemId);
            if (!isItemInShortlist) {
                const updatedPeople = [...(Array.isArray(prev.people) ? prev.people : []), formattedItem];
                handleChange(PEOPLE, { ...prev, people: prev.people });
                return { ...prev, people: updatedPeople };
            }
        } else if (PERSON in prev) {
            const isItemSelected = prev?.person?.id === itemId;
            if (!isItemSelected) {
                handleChange(PERSON, formattedItem);
                return { ...prev, person: formattedItem };
            }
        }
        return prev;
    };

    const MarketplacePanelContent = () => {
        const { clickedEngagement, setClickedEngagement, clickedPerson, setClickedPerson } = useEngagementTracker();
        const {
            clickedPersonLoading,
            displayItem,
            productionDetailsData,
            productionDetailsLoading,
            showEngagementProductionDetails,
            noteId,
            showNoteDetails,
            noteEntityReference
        } = useMarketplaceEngagementDetails({
            clickedPerson,
            selectedItem,
            clickedEngagement,
            detailsVisible
        });

        const noteDetailsBody =
            showNoteDetails &&
            noteId &&
            noteEntityReference &&
            isEntityName(displayItem?.reference?.type) &&
            displayItem?.security ? (
                <MarketplaceEngagementNoteDetails
                    id="notes-hiring-manager-marketplace"
                    noteId={noteId}
                    entity={displayItem.reference.type}
                    entitySecurity={displayItem.security}
                    entityReference={noteEntityReference}
                    onClose={() => setClickedEngagement(null)}
                />
            ) : null;

        return (
            <V fill sx={{ width: '100%' }}>
                <H sx={{ width: '100%', justifyContent: 'space-between', px: 1 }}>
                    <Typography fontSize={16}>{t('talent.hub')}</Typography>
                    <H sx={{ width: 'unset' }}>
                        {detailsVisible && (
                            <IconButton
                                onClick={() => {
                                    if (showNoteDetails) {
                                        setClickedEngagement(null);
                                        return;
                                    }
                                    if (showEngagementProductionDetails) {
                                        setClickedEngagement(null);
                                        return;
                                    }
                                    if (clickedPerson) {
                                        setClickedPerson(null);
                                        return;
                                    }
                                    setDetailsVisible(false);
                                }}
                            >
                                <Icons.BackButton />
                            </IconButton>
                        )}
                        <IconButton
                            onClick={() => {
                                setClickedEngagement(null);
                                setClickedPerson(null);
                                expandMarketplace();
                                setIsMarketplaceOpen(false);
                            }}
                        >
                            <Icons.Close />
                        </IconButton>
                    </H>
                </H>
                <V fill sx={{ overflow: 'auto' }}>
                    {noteDetailsBody ? (
                        noteDetailsBody
                    ) : clickedPerson && clickedPersonLoading ? (
                        <Loading />
                    ) : showEngagementProductionDetails &&
                      (productionDetailsLoading || !productionDetailsData?.production) ? (
                        <Loading />
                    ) : showEngagementProductionDetails ? (
                        <ProductionDetailsPanelBody
                            viewState={productionDetailsData.production}
                            statusNote={{ enableCurrentNote: false }}
                        />
                    ) : detailsVisible ? (
                        <MarketplaceProfileDetails
                            item={displayItem}
                            shortlistLineItems={shortlistLineItems}
                            onAddToShortlist={addFromShortlist}
                            isOrganizationDetails={false}
                            isProfilePreview
                        />
                    ) : (
                        <PersonMarketplaceList
                            id="ppl-hire-crew-marketplace"
                            onAddToShortlist={(item) => addFromShortlist(item)}
                            shortlistLineItems={shortlistLineItems}
                            immutableFilters={personPrefilters}
                            handleItemClick={(item) => handleItemSelection(item, setSelectedItem, setDetailsVisible)}
                            orgRole={resourceData?.role}
                        />
                    )}
                </V>
            </V>
        );
    };

    const MarketplacePanel = () => (
        <EngagementTrackerProvider>
            <MarketplacePanelContent />
        </EngagementTrackerProvider>
    );

    return (
        <H sx={{ flexGrow: 1, overflow: 'hidden' }}>
            <V fill>
                <Button
                    onClick={() => {
                        expandMarketplace();
                        setIsMarketplaceOpen((prev) => !prev);
                    }}
                    sx={{ display: 'flex', gap: 1, mt: 1, ml: 2 }}
                >
                    <Icons.Search />
                    {t('talent.hub')}
                </Button>

                {!isSmallScreen || !isMarketplaceOpen ? <PersonInviteFormItems {...props} /> : <></>}
            </V>
            {isMarketplaceOpen && <MarketplacePanel />}
        </H>
    );
}
